import cn from 'classnames';
import { MutableRefObject } from 'react';

import { AutoshipmentFodBanner } from '@zooplus/autoshipment-checkout-front';
import { useCheckoutContext, Article, Shipment } from '@zooplus/checkout-state';
import { SamplesComponent } from '@zooplus/samples-component';
import { FlatBox } from '@zooplus/ui-components';

import FeedbackMessage from '../../common/notifications/feedback-message';

import { useTranslation } from '@hooks/use-translations';
import commonStyles from '@styles/common.module.scss';
import { ARTICLE_TYPE, MESSAGE_PLACEMENT, CHECKOUT_STATE_PROJECT_ID } from '@utilities/constants';
import { isArrayNotEmpty } from '@utilities/utils';

import CartItem from './cart-item/index';
import CartShipment from './cart-shipment';
import '@zooplus/samples-component/dist/es/shsvs-samples-component.min.css';
import styles from './cart-table.module.scss';

type CartTableProps = {
  keyCloackToken: MutableRefObject<string | undefined>;
  has3pArticles: boolean;
};

const CartTable = ({ keyCloackToken, has3pArticles }: CartTableProps) => {
  const {
    checkoutState,
    mainTogglesMap = {},
    isFetching,
    setFreeSampleArticle,
  } = useCheckoutContext(CHECKOUT_STATE_PROJECT_ID);
  const { t } = useTranslation();
  const isFreeSamplesEnabled = mainTogglesMap.CHECKOUT_OB_SAMPLES_INTEGRATION_FEATURE_ENABLED;
  const articles = checkoutState?.cart?.articles ?? [];
  const articleIds = articles.map((article) => article.id);
  const customerId = checkoutState?.customer?.customerId;
  const hasArticles = isArrayNotEmpty(articles as Article[]);
  const hasFreeSample = articles.some((article) => article.type === ARTICLE_TYPE.FREE_SAMPLE);
  const showFreeSampleArticle = hasArticles && isFreeSamplesEnabled && !hasFreeSample;
  const shipments = checkoutState?.cart?.shipments;
  const shipmentWithArticles = shipments?.filter(
    (shipment: Shipment) => shipment?.articles?.length,
  );
  const hasOnly1pArticles = hasArticles && !has3pArticles;
  const { estimatedDeliveryDays, estimatedDeliveryDaysKey = '' } = checkoutState?.delivery || {};
  const hasTableHeaderMessage = checkoutState?.cart?.cartMessages?.find?.((message) =>
    message?.placements.includes(MESSAGE_PLACEMENT.CART_TABLE_HEADER),
  );
  const isFirstOrderDiscountAvailable = articles.some(
    (article) => article.subscriptionDetails?.firstOrderDiscount,
  );

  const getArticlesMap = (items: Article[] | undefined) =>
    items?.map((article: Article) => <CartItem {...article} key={article.id} />);

  const getShipmentMap = () =>
    shipmentWithArticles?.map((shipment: Shipment, index: number) => {
      const is1P: boolean = [shipment.orderType, shipment.seller.sellerType].includes('1P');

      return (
        <CartShipment
          key={shipment.id}
          seller={shipment.seller}
          id={shipment.id}
          index={index}
          totalShipments={shipmentWithArticles?.length}
          is1P={is1P}
          shippingCost={shipment.selectedShippingType?.shippingCost}
          shipmentTotal={shipment.shipmentTotal}
          keyCloackToken={keyCloackToken}
          isFirstOrderDiscountAvailable={isFirstOrderDiscountAvailable}>
          {getArticlesMap(shipment.articles)}
        </CartShipment>
      );
    });

  return (
    <FlatBox
      className={cn(styles.cartTable, has3pArticles && styles.cartTableNoBorder)}
      rounded
      variant="white">
      {hasOnly1pArticles && (
        <FlatBox
          className={styles.cartTableHeader}
          data-testid="cart-table-header"
          variant="neutral"
          rounded>
          <h2 className={styles.cartTableHeaderTitle}>{t('cart.table.header.title')}</h2>
          {!!estimatedDeliveryDays && (
            <p className={cn(styles.estimatedDeliveryDays, 'pp-m-0')}>
              ({t(estimatedDeliveryDaysKey, { 0: estimatedDeliveryDays })})
            </p>
          )}
        </FlatBox>
      )}

      {hasOnly1pArticles && (
        <div className={cn(!isFirstOrderDiscountAvailable && commonStyles.hideWrapper)}>
          {!isFetching && <AutoshipmentFodBanner />}
        </div>
      )}

      {hasTableHeaderMessage && <FeedbackMessage placement={MESSAGE_PLACEMENT.CART_TABLE_HEADER} />}
      {has3pArticles && getShipmentMap()}
      {hasOnly1pArticles && getArticlesMap(articles)}
      {showFreeSampleArticle && (
        <SamplesComponent
          cartArticlesId={articleIds}
          customerId={customerId}
          setFreeSampleArticle={setFreeSampleArticle ?? (() => {})}
        />
      )}
    </FlatBox>
  );
};

export default CartTable;
